import React, { useEffect, useState, useRef, useMemo } from "react";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import { DateSelector } from "../../components/DateSelector/DateSelector";
import { SpinLoader } from "../../components/loader/Loader";
import { ColDef, ICellRendererParams } from "ag-grid-community";
import { Link } from "react-router-dom";
import { useAuth } from "../../auth/AuthProvider";
import { useTrainingStore, usePremisesStore } from "../../store/store";
import { Training, Trainee, Test } from "../../types/api";
import {
  Search,
  Users,
  Calendar,
  CheckCircle,
  Trash2,
  AlertTriangle,
} from "lucide-react";
import images from "../../assets/images";
import { debounce } from "lodash";
import { getS3SignedUrl } from "../../utils/s3Client";
import DocumentViewer from "../../components/DocumentViewer/DocumentViewer";
import ConfirmModal from "../../components/modal/ConfirmModal";
import { XIcon } from "lucide-react";
import { useTrainingModule } from "./TrainingModuleContext";
import { Dropdown } from "../../components/dropdown/Dropdown";
import type { TrainingModulePaths } from "./moduleConfigs";

interface CustomUser {
  id?: string;
  roles?: string[];
}

type TestCategory = "Pre-Test" | "Post-Test";

const TestCategorySelect: React.FC<{
  value: TestCategory;
  onChange: (value: TestCategory) => void;
}> = ({ value, onChange }) => (
  <select
    value={value}
    onChange={(e) => onChange(e.target.value as TestCategory)}
    className="border border-gray-300 rounded px-1 py-1 text-sm"
  >
    <option value="Pre-Test">Pre-Test</option>
    <option value="Post-Test">Post-Test</option>
  </select>
);

const TestAvailabilityCell: React.FC<{
  data: Training;
  category: TestCategory;
  paths: TrainingModulePaths;
}> = ({ data, category, paths }) => {
  const tests: Test[] = data.tests || [];
  const test = tests.find((t) => t.testCategory === category);

  return (
    <div className="flex items-center gap-2">
      <span>{test ? "Yes" : "No"}</span>
      {test ? (
        <Link to={paths.startTest(test.id)}>
          <button className="bg-[#0fa9a6] text-white px-3 py-1 rounded-full hover:bg-teal-700 text-xs whitespace-nowrap">
            Start Test
          </button>
        </Link>
      ) : (
        <Link
          to={`${paths.createTest}?trainingId=${data.id}&testCategory=${category}`}
        >
          <button className="bg-[#4b6cb9] text-white px-3 py-1 rounded-full hover:bg-teal-700 text-xs whitespace-nowrap">
            Create Test
          </button>
        </Link>
      )}
    </div>
  );
};

const TrainingDashboard: React.FC = () => {
  const { user } = useAuth() as { user: CustomUser };
  const { moduleType, validCategories, paths } = useTrainingModule();
  const isFixedCategory = validCategories.length === 1;
  const { trainings, fetchTrainings, loading, error, deleteTraining } =
    useTrainingStore();

  // Keyed by training id, so the "Test Category" and "Test Availability"
  // columns stay in sync for the same row.
  const [testCategoryByTraining, setTestCategoryByTraining] = useState<
    Record<string, TestCategory>
  >({});
  const getSelectedTestCategory = (trainingId: string): TestCategory =>
    testCategoryByTraining[trainingId] || "Pre-Test";

  const [trainingToDelete, setTrainingToDelete] = useState<{
    id: string;
    topic: string;
  } | null>(null);

  const handleDeleteTraining = (id: string, topic: string) => {
    setTrainingToDelete({ id, topic });
  };

  const confirmDeleteTraining = async () => {
    const target = trainingToDelete;
    if (!target) return;
    setTrainingToDelete(null);
    try {
      await deleteTraining(target.id);
    } catch (err) {
      console.error("Error deleting training:", err);
    }
  };
  const { premiseMap, fetchPremises } = usePremisesStore();
  const [selectedRegion, setSelectedRegion] = useState("");
  const [selectedPremises, setSelectedPremises] = useState("");
  const [dateRange, setDateRange] = useState<[Date | null, Date | null]>([
    null,
    null,
  ]);
  const [filterStartDate, filterEndDate] = dateRange;
  useEffect(() => {
    fetchPremises();
  }, [fetchPremises]);
  const [searchTerm, setSearchTerm] = useState("");
  const [viewMode, setViewMode] = useState<"trainee" | "trainer">("trainee");
  const currentGridRef = useRef<AgGridReact>(null);
  const [renderKey, setRenderKey] = useState(Date.now());
  const [filePreview, setFilePreview] = useState<{ url: string; name: string }>(
    {
      url: "",
      name: "",
    },
  );
  const [stats, setStats] = useState<{
    totalTrainings: number;
    upcomingSessionsCount: number;
    pastTrainingsCount: number;
    pendingSessionsCount: number;
  } | null>(null);

  const userRoles = user?.roles || [];
  const effectiveRole = userRoles.includes("orgadmin")
    ? "orgadmin"
    : userRoles.includes("orguser")
      ? "orguser"
      : "trainee";
  const isTrainer =
    effectiveRole === "orgadmin" ||
    (effectiveRole === "orguser" && viewMode === "trainer");
  const isOrgAdmin = effectiveRole === "orgadmin"; // New flag to differentiate orgadmin

  const today = useMemo(() => {
    const d = new Date();
    d.setUTCHours(0, 0, 0, 0);
    return d;
  }, []);

  const handleSearch = useMemo(
    () =>
      debounce((keyword: string) => {
        setSearchTerm(keyword);
      }, 300),
    [],
  );

  useEffect(() => {
    return () => {
      handleSearch.cancel();
    };
  }, [handleSearch]);

  useEffect(() => {
    if (effectiveRole !== "orguser") return;
    const saved = localStorage.getItem("training_view_mode");
    if (!saved) {
      localStorage.setItem("training_view_mode", "trainee");
      setViewMode("trainee");
      return;
    }
    if (saved === "trainer" || saved === "trainee") {
      setViewMode(saved);
    }
  }, []);

  const handleViewModeChange = (mode: "trainer" | "trainee") => {
    localStorage.setItem("training_view_mode", mode);
    setViewMode(mode);
  };

  // Region/premises/date-range/role scoping is now all done server-side
  // (TrainingController.js's `all` action) instead of fetching everything
  // and re-filtering client-side — refetch whenever any of these change.
  useEffect(() => {
    (async () => {
      const result = await fetchTrainings({
        type: "all",
        where: isFixedCategory
          ? { moduleType, category: validCategories[0] }
          : { moduleType },
        region: selectedRegion || undefined,
        premises: selectedPremises || undefined,
        // startDate: filterStartDate ? filterStartDate.toISOString() : undefined,
        // endDate: filterEndDate ? filterEndDate.toISOString() : undefined,
        roleFilter:
          effectiveRole === "orguser"
            ? viewMode === "trainer"
              ? "trainer"
              : "trainee"
            : undefined,
        includeStats: isOrgAdmin,
      });
      if (result?.stats) setStats(result.stats);
    })();
  }, [
    fetchTrainings,
    moduleType,
    isFixedCategory,
    validCategories,
    selectedRegion,
    selectedPremises,
    // filterStartDate,
    // filterEndDate,
    effectiveRole,
    viewMode,
    isOrgAdmin,
  ]);

  // `trainings` from the store is already scoped (module/region/premises/
  // date/role) — just apply the "still needs a Post-Test" completion rule,
  // using the canonical hasPostTestCompleted field computed server-side
  // instead of the (buggy) test.status signal.
  const currentSessions = useMemo(() => {
    if (!Array.isArray(trainings)) return [];
    if (isTrainer) {
      return trainings.filter((t) => !t.hasPostTestCompleted);
    }
    return trainings.filter((t) => {
      const trainee = t.trainees?.find((tr) => tr.traineeId === user?.id);
      const hasSubmittedPostTest =
        trainee?.results?.postTestScore !== null &&
        trainee?.results?.postTestScore !== undefined;
      return t.isUpcoming || !hasSubmittedPostTest;
    });
  }, [trainings, isTrainer, user?.id]);

  const filteredCurrentSessions = useMemo(() => {
    return currentSessions.filter((session) => {
      // Search
      if (
        searchTerm &&
        !session.topic?.toLowerCase().includes(searchTerm.toLowerCase())
      ) {
        return false;
      }

      // Date
      if (filterStartDate && filterEndDate) {
        if (!session.date) return false;

        const sessionDate = new Date(session.date);

        const start = new Date(filterStartDate);
        start.setHours(0, 0, 0, 0);

        const end = new Date(filterEndDate);
        end.setHours(23, 59, 59, 999);

        return sessionDate >= start && sessionDate <= end;
      }

      return true;
    });
  }, [currentSessions, searchTerm, filterStartDate, filterEndDate]);

  // Only meaningful once `stats` (org-admin only) has loaded.
  const trainingCompletionRate =
    stats && stats.totalTrainings > 0
      ? ((stats.pastTrainingsCount / stats.totalTrainings) * 100).toFixed(0)
      : 0;

  const handleViewFile = async (fileUrl: string) => {
    try {
      const key = fileUrl.replace(
        "https://dev-kc-repo.s3.ap-south-1.amazonaws.com/",
        "",
      );

      // Get extension to determine MIME type
      const ext = key.split(".").pop()?.toLowerCase();
      const mimeTypes: Record<string, string> = {
        pdf: "application/pdf",
        doc: "application/msword",
        docx: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
        txt: "text/plain",
        jpg: "image/jpeg",
        png: "image/png",
      };
      const mime = mimeTypes[ext || ""] || "application/octet-stream";

      const signed = await getS3SignedUrl(key, mime);
      setFilePreview({ url: signed, name: key.split("/").pop() || "document" });
    } catch (error) {
      console.error("Error viewing file:", error);
    }
  };
  const handleCloseFile = () => {
    setFilePreview({ url: "", name: "" });
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  };

  // Prefer the actual start/end time picked at creation; legacy trainings
  // only ever persisted total minutes, so synthesize a range from a
  // default 09:00 start instead of showing a bare minute count.
  const DEFAULT_TRAINING_START_TIME = "09:00";
  const formatTrainingTime = (training: Training): string => {
    if (training.startTime && training.endTime) {
      return `${training.startTime}-${training.endTime}`;
    }
    const total = parseInt(training.duration || "0", 10) || 0;
    if (!total) return "-";
    const [h, m] = DEFAULT_TRAINING_START_TIME.split(":").map(Number);
    const endMinutes = (((h * 60 + m + total) % 1440) + 1440) % 1440;
    const endTime = `${String(Math.floor(endMinutes / 60)).padStart(2, "0")}:${String(
      endMinutes % 60,
    ).padStart(2, "0")}`;
    return `${DEFAULT_TRAINING_START_TIME}-${endTime}`;
  };

  const trainerColumnDefs: ColDef[] = [
    { headerName: "Topic", field: "topic", flex: 1, sortable: true },
    { headerName: "Category", field: "category", flex: 1, sortable: true },
    {
      headerName: "Premises",
      field: "premises",
      flex: 1,
      sortable: true,
      valueGetter: (params) => {
        const premiseIds: string[] = params.data.premises || [];
        if (premiseIds.length === 0) return "All Premises";
        return premiseIds.map((id) => premiseMap?.[id] || id).join(", ");
      },
    },
    {
      headerName: "Date",
      field: "date",
      flex: 1,
      sortable: true,
      valueFormatter: (params) => formatDate(params.value),
    },
    {
      headerName: "Timings",
      field: "duration",
      flex: 1,
      sortable: true,
      valueGetter: (params) => formatTrainingTime(params.data),
    },
    {
      headerName: "Total Trainees",
      field: "trainees",
      flex: 1,
      sortable: true,
      valueGetter: (params) => params.data.trainees?.length || 0,
    },
    {
      headerName: "Test Category",
      field: "testCategory",
      flex: 1,
      cellRenderer: (params: any) => (
        <TestCategorySelect
          value={getSelectedTestCategory(params.data.id)}
          onChange={(value) =>
            setTestCategoryByTraining((prev) => ({
              ...prev,
              [params.data.id]: value,
            }))
          }
        />
      ),
    },
    {
      headerName: "Test Availability",
      field: "testAvailability",
      flex: 1.4,
      cellRenderer: (params: any) => (
        <TestAvailabilityCell
          data={params.data}
          category={getSelectedTestCategory(params.data.id)}
          paths={paths}
        />
      ),
    },
    {
      headerName: "Action",
      field: "action",
      flex: 1,
      cellRenderer: (params: any) => {
        return (
          <div className="flex items-center gap-2">
            <Link to={paths.trainingView(params.data.id)}>
              <img className="iconTd" src={images.viewIcon} alt="View" />
            </Link>
            <Link to={paths.editTraining(params.data.id)}>
              <img className="iconTd" src={images.editIcon} alt="Edit" />
            </Link>
            <button
              type="button"
              onClick={() =>
                handleDeleteTraining(params.data.id, params.data.topic)
              }
              title="Delete"
              className="text-red-500 hover:text-red-700"
            >
              <Trash2 size={18} />
            </button>
          </div>
        );
      },
    },
  ];

  const traineeColumnDefs: ColDef[] = [
    { headerName: "Topic", field: "topic", flex: 1, sortable: true },
    {
      // Server-computed (TrainingController.js's computeTrainingDerivedFields)
      // instead of re-deriving it from raw test.status here.
      headerName: "Category",
      field: "displayedTestCategory",
      flex: 1,
      sortable: true,
    },
    {
      headerName: "Date",
      field: "date",
      flex: 1,
      sortable: true,
      valueFormatter: (params) => {
        const tests: Test[] = params.data.tests || [];
        const test = tests.find(
          (t) => t.testCategory === params.data.displayedTestCategory,
        );
        return formatDate(test?.date || params.data.date);
      },
    },
    {
      headerName: "Material",
      field: "files",
      flex: 1,
      cellRenderer: (params: ICellRendererParams) => {
        const files: string[] = params.data.files;

        if (!files || files.length === 0) {
          return <span>No files</span>;
        }

        return (
          <button
            onClick={() => handleViewFile(files[0])}
            className="text-[#0fa9a6] font-semibold underline"
          >
            View
          </button>
        );
      },
      //  : (
      //   <span>No files</span>
      // ),
    },
    {
      headerName: "Pre-Test Score",
      field: "preTestScore",
      flex: 1,
      valueGetter: (params) => {
        const trainee = params.data.trainees.find(
          (t: Trainee) => t.traineeId === user?.id,
        );
        return trainee?.results?.preTestScore ?? "N/A";
      },
      valueFormatter: (params) =>
        params.value !== "N/A" ? `${Math.round(params.value)}%` : "N/A",
    },
    {
      headerName: "Post-Test Score",
      field: "postTestScore",
      flex: 1,
      valueGetter: (params) => {
        const trainee = params.data.trainees.find(
          (t: Trainee) => t.traineeId === user?.id,
        );
        return trainee?.results?.postTestScore ?? "N/A";
      },
      valueFormatter: (params) =>
        params.value !== "N/A" ? `${Math.round(params.value)}%` : "N/A",
    },
    {
      headerName: "Action",
      field: "action",
      flex: 1,
      cellRenderer: (params: any) => {
        const trainee = params.data.trainees.find(
          (t: Trainee) => t.traineeId === user?.id,
        );
        const tests: Test[] = params.data.tests || [];
        // Find both pre-test and post-test
        const preTest = tests.find((t) => t.testCategory === "Pre-Test");
        const postTest = tests.find((t) => t.testCategory === "Post-Test");
        const preTestSubmitted =
          trainee?.results?.preTestScore !== null &&
          trainee?.results?.preTestScore !== undefined;
        const postTestSubmitted =
          trainee?.results?.postTestScore !== null &&
          trainee?.results?.postTestScore !== undefined;
        // Show post-test donut if post-test submitted
        if (postTestSubmitted) {
          const scoreInt = Math.round(trainee?.results?.postTestScore);
          return (
            <div className="flex flex-row items-center gap-2">
              <span className="font-medium text-black">Score</span>
              <svg width="46" height="46" viewBox="0 0 40 40">
                <circle
                  cx="20"
                  cy="20"
                  r="16"
                  fill="none"
                  stroke="#e5e7eb"
                  strokeWidth="4"
                />
                <circle
                  cx="20"
                  cy="20"
                  r="16"
                  fill="none"
                  stroke="#22c55e"
                  strokeWidth="4"
                  strokeDasharray={2 * Math.PI * 16}
                  strokeDashoffset={2 * Math.PI * 16 * (1 - scoreInt / 100)}
                  strokeLinecap="round"
                  style={{ transition: "stroke-dashoffset 0.5s" }}
                />
                <text
                  x="50%"
                  y="50%"
                  textAnchor="middle"
                  dominantBaseline="central"
                  fontSize="10"
                  fontWeight="bold"
                  fill="#222"
                >
                  {scoreInt}%
                </text>
              </svg>
            </div>
          );
        }
        // If post-test is available (attendance present, date is today), show Start Test for post-test
        const postTestIsPresent =
          trainee?.attendance?.["Post-Test"] === "Present";
        const postTestIsToday = postTest?.date
          ? new Date(postTest.date).setUTCHours(0, 0, 0, 0) === today.getTime()
          : false;
        if (
          preTestSubmitted &&
          postTest &&
          postTestIsPresent &&
          postTestIsToday
        ) {
          return (
            <Link to={paths.attemptTest(postTest.id)}>
              <button className="bg-[#0fa9a6] text-white px-4 py-1 rounded-full uppercase text-sm font-semibold hover:bg-teal-700">
                Start Test
              </button>
            </Link>
          );
        }
        // If pre-test is submitted but post-test is not available, show pre-test donut
        if (preTestSubmitted) {
          const scoreInt = Math.round(trainee?.results?.preTestScore);
          return (
            <div className="flex flex-row items-center gap-2">
              <span className="font-medium text-black">Score</span>
              <svg width="46" height="46" viewBox="0 0 40 40">
                <circle
                  cx="20"
                  cy="20"
                  r="16"
                  fill="none"
                  stroke="#e5e7eb"
                  strokeWidth="4"
                />
                <circle
                  cx="20"
                  cy="20"
                  r="16"
                  fill="none"
                  stroke="#22c55e"
                  strokeWidth="4"
                  strokeDasharray={2 * Math.PI * 16}
                  strokeDashoffset={2 * Math.PI * 16 * (1 - scoreInt / 100)}
                  strokeLinecap="round"
                  style={{ transition: "stroke-dashoffset 0.5s" }}
                />
                <text
                  x="50%"
                  y="50%"
                  textAnchor="middle"
                  dominantBaseline="central"
                  fontSize="10"
                  fontWeight="bold"
                  fill="#222"
                >
                  {scoreInt}%
                </text>
              </svg>
            </div>
          );
        }
        // If not submitted, show Start Test for pre-test if eligible
        const preTestIsPresent =
          trainee?.attendance?.["Pre-Test"] === "Present";
        const preTestIsToday = preTest?.date
          ? new Date(preTest.date).setUTCHours(0, 0, 0, 0) === today.getTime()
          : false;
        if (preTest && preTestIsPresent && preTestIsToday) {
          return (
            <Link to={paths.attemptTest(preTest.id)}>
              <button className="bg-[#0fa9a6] text-white px-4 py-1 rounded-full uppercase text-sm font-semibold hover:bg-teal-700">
                Start Test
              </button>
            </Link>
          );
        }
        return <span>Test not available</span>;
      },
    },
  ];

  // Only block the whole screen on the very first load. `loading` also
  // flips true on every filter-triggered refetch (region/premises/date),
  // and re-mounting the entire page — filters included — on each of those
  // made picking a filter feel like a full page reload.
  if (loading && (!Array.isArray(trainings) || trainings.length === 0)) {
    return <div className="text-center py-8 text-gray-500">Loading...</div>;
  }

  if (error) {
    return <div className="text-red-500 text-center py-8">Error: {error}</div>;
  }

  return (
    <div className="px-2 py-4 mt-20">
      <div className="flex justify-between items-center mb-4">
        <h1 className="text-2xl font-semibold text-[#5a687c]">
          {isTrainer ? "Training Dashboard" : "Your Training Dashboard"}
        </h1>
        <div className="flex gap-4">
          {effectiveRole === "orguser" && (
            <select
              value={viewMode}
              onChange={(e) =>
                handleViewModeChange(e.target.value as "trainee" | "trainer")
              }
              className="border-2 border-[#a5b7cf] p-2 rounded"
            >
              <option value="trainee">Trainee View</option>
              <option value="trainer">Trainer View</option>
            </select>
          )}
          {/* User Training Analytics Button - visible to org admins only */}
          {isOrgAdmin && (
            <Link to={paths.userAnalytics}>
              <button className="flex items-center justify-center font-semibold gap-3 bg-[#0fa9a6] text-white px-4 py-2 rounded-full hover:bg-[#3a5a9a] transition-colors">
                <img
                  src={images.assetsIcon}
                  alt=""
                  className="w-5 h-5 text-white"
                />
                USER TRAINING ANALYSIS
              </button>
            </Link>
          )}

          {isTrainer && (
            <Link to={paths.trainerStartTest}>
              <button className="flex items-center justify-center font-semibold gap-3 bg-[#0fa9a6] text-white px-4 py-2 rounded-full hover:bg-[#3a5a9a] transition-colors">
                <img
                  src={images.addPremiessIcon}
                  alt=""
                  className="w-6 h-6 text-white"
                />
                START TEST
              </button>
            </Link>
          )}

          {isOrgAdmin && (
            <>
              <Link to={paths.createTest}>
                <button className="flex items-center justify-center font-semibold gap-3 bg-[#0fa9a6] text-white px-4 py-2 rounded-full">
                  <img
                    src={images.addPremiessIcon}
                    alt=""
                    className="w-6 h-6 text-white"
                  />
                  CREATE TEST
                </button>
              </Link>
              <Link to={paths.createTraining}>
                <button className="flex items-center justify-center font-semibold gap-3 bg-[#0fa9a6] text-white px-4 py-2 rounded-full">
                  <img
                    src={images.addPremiessIcon}
                    alt=""
                    className="w-6 h-6 text-white"
                  />
                  CREATE TRAINING PLAN
                </button>
              </Link>
            </>
          )}
        </div>
      </div>

      {isOrgAdmin && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white p-6 rounded-lg shadow-md border-l-4 border-[#0fa9a6]">
            <div className="flex items-center">
              <Users className="w-8 h-8 text-[#0fa9a6] mr-3" />
              <div>
                <h3 className="text-xl font-semibold text-gray-700">
                  Total Sessions
                </h3>
                <p className="text-3xl font-bold text-[#0fa9a6]">
                  {stats?.totalTrainings ?? 0}
                </p>
                <p className="text-sm text-gray-400 mt-1">
                  Upcoming: {stats?.upcomingSessionsCount ?? 0} · Past:{" "}
                  {stats?.pastTrainingsCount ?? 0}
                </p>
              </div>
            </div>
          </div>
          <div className="bg-white p-6 rounded-lg shadow-md border-l-4 border-[#0fa9a6]">
            <div className="flex items-center">
              <Calendar className="w-8 h-8 text-[#0fa9a6] mr-3" />
              <div>
                <h3 className="text-xl font-semibold text-gray-700">
                  Upcoming Sessions
                </h3>
                <p className="text-3xl font-bold text-[#0fa9a6]">
                  {stats?.upcomingSessionsCount ?? 0}
                </p>
              </div>
            </div>
          </div>
          <div className="bg-white p-6 rounded-lg shadow-md border-l-4 border-[#0fa9a6]">
            <div className="flex items-center">
              <CheckCircle className="w-8 h-8 text-[#0fa9a6] mr-3" />
              <div>
                <h3 className="text-xl font-semibold text-gray-700">
                  Training Completion Rate
                </h3>
                <p className="text-3xl font-bold text-[#0fa9a6]">
                  {trainingCompletionRate}%
                </p>
              </div>
            </div>
          </div>
          <div className="bg-white p-6 rounded-lg shadow-md border-l-4 border-red-500">
            <div className="flex items-center">
              <AlertTriangle className="w-8 h-8 text-red-500 mr-3" />
              <div>
                <h3 className="text-xl font-semibold text-gray-700">
                  Pending Sessions
                </h3>
                <p className="text-3xl font-bold text-red-600">
                  {stats?.pendingSessionsCount ?? 0}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="bg-white rounded-lg shadow-md p-4">
        <div className="flex flex-col md:flex-row justify-between items-center mb-4">
          <div className="flex items-center gap-3">
            <h2 className="text-xl font-semibold text-gray-700 mb-1">
              Current Sessions
            </h2>
          </div>
        </div>
        <div className="flex items-center gap-3 mb-4 overflow-visible relative z-30">
          {/* Search */}
          <div className="relative w-[40%] max-w-md">
            <Search className="w-5 h-5 text-[#61748e] absolute left-3 top-1/2 transform -translate-y-1/2 pointer-events-none" />
            <input
              type="text"
              placeholder="Search"
              value={searchTerm}
              onChange={(e) => handleSearch(e.target.value)}
              className="w-full bg-white border-2 border-[#a5b7cf] rounded-lg pl-10 pr-4 py-2 text-[#5a687c] placeholder:text-[#666768]  outline-none focus:outline-none"
            />
          </div>
          {/* Region + Premises */}
          <div className="flex items-center gap-3 ml-auto flex-shrink-0">
            <Dropdown
              selectedValue={selectedRegion}
              onChange={(value) => {
                setSelectedRegion(value);
                setSelectedPremises("");
              }}
              type="region"
            />
            <Dropdown
              selectedValue={selectedPremises}
              onChange={setSelectedPremises}
              type="premises"
              selectedRegion={selectedRegion}
            />
            {/* <div className="flex-shrink-0">
              <DateSelector
                onDateChange={(start, end) => setDateRange([start, end])}
              />
            </div> */}
          </div>
        </div>
        {loading ? (
          <div className="flex justify-center items-center h-64">
            <SpinLoader />
          </div>
        ) : (
          <div className="ag-theme-alpine" style={{ height: "500px" }}>
            <AgGridReact
              ref={currentGridRef}
              rowData={filteredCurrentSessions}
              columnDefs={isTrainer ? trainerColumnDefs : traineeColumnDefs}
              rowHeight={50}
              headerHeight={65}
              pagination={true}
              paginationPageSize={10}
              domLayout="autoHeight"
              defaultColDef={{
                sortable: true,
                resizable: true,
              }}
              getRowStyle={() => ({
                boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                marginBottom: "8px",
                borderRadius: "4px",
                backgroundColor: "white",
              })}
            />
          </div>
        )}
        {filePreview.url !== "" && (
          <div
            className="absmiddle"
            style={{
              width: "100vw",
              height: "100vh",
              zIndex: 999999,
              backgroundColor: "rgba(0,0,0,0.5)",
            }}
          >
            <XIcon
              className="absolute right-4 top-2 z-10 cursor-pointer bg-red-500 text-white"
              style={{ right: "115px" }}
              onClick={handleCloseFile}
            />
            <div
              className="absmiddle bg-white border py-9"
              style={{
                width: "calc(100vw - 200px)",
                height: "100vh",
                overflow: "auto",
                boxSizing: "border-box",
              }}
            >
              <DocumentViewer file={filePreview.url} name={filePreview.name} />
            </div>
          </div>
        )}
      </div>
      <ConfirmModal
        isOpen={!!trainingToDelete}
        title={`Are you sure you want to delete "${trainingToDelete?.topic}"?`}
        onConfirm={confirmDeleteTraining}
        onCancel={() => setTrainingToDelete(null)}
      />
    </div>
  );
};

export default React.memo(TrainingDashboard);
